import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cuentaRegresiva, enAviso, type Vigencia } from './inactividad';
import { abrirComoModal, cerrarModal } from '../components/Modal';

interface Props {
  vigencia: Vigencia;
  onSeguir: () => Promise<void>;
  onSalir: () => void;
}

/**
 * Aviso de que la sesión está por cerrarse por inactividad, con «Sigo aquí».
 *
 * No usa `Modal`: ésa se cierra con un clic en el fondo, y aquí cerrar el aviso
 * sin contestarlo dejaría a la persona creyendo que siguió. Escape cuenta como
 * respuesta —quien lo pulsa está enfrente—, y el foco arranca en «Sigo aquí»,
 * no en «Salir»: un Enter distraído no debe sacar a nadie.
 */
export function AvisoDeSesion({ vigencia, onSeguir, onSalir }: Props) {
  const { t } = useTranslation();
  const ventana = useRef<HTMLDialogElement>(null);
  const principal = useRef<HTMLButtonElement>(null);
  const [ahora, setAhora] = useState(() => Date.now());
  const [ocupado, setOcupado] = useState(false);
  // Sin «Sigo aquí» posible, el aviso se puede cerrar; no vuelve a salir para el mismo cierre.
  const [enteradoDe, setEnteradoDe] = useState<number | null>(null);

  useEffect(() => {
    const reloj = window.setInterval(() => setAhora(Date.now()), 1_000);
    return () => window.clearInterval(reloj);
  }, []);

  const restante = vigencia.plazo - ahora;
  const visible = enAviso(restante) && enteradoDe !== vigencia.plazo;
  const tiempo = cuentaRegresiva(restante);

  useEffect(() => {
    const elemento = ventana.current;
    if (!elemento) return;
    if (visible && !elemento.open) {
      abrirComoModal(elemento);
      principal.current?.focus();
    }
    if (!visible && elemento.open) cerrarModal(elemento);
  }, [visible]);

  // En otra pestaña el aviso no se ve; el título sí.
  useEffect(() => {
    if (!visible) return;
    const original = document.title;
    return () => {
      document.title = original;
    };
  }, [visible]);
  useEffect(() => {
    if (visible) document.title = t('session.expiringTab', { tiempo });
  }, [visible, tiempo, t]);

  const contestar = () => {
    if (!vigencia.extensible) {
      setEnteradoDe(vigencia.plazo);
      return;
    }
    if (ocupado) return;
    setOcupado(true);
    void onSeguir().finally(() => setOcupado(false));
  };

  return (
    <dialog
      ref={ventana}
      className="modal aviso-sesion"
      aria-labelledby="aviso-sesion-titulo"
      aria-describedby="aviso-sesion-texto"
      onCancel={(evento) => {
        evento.preventDefault();
        contestar();
      }}
    >
      {visible && (
        <div className="modal__cuerpo">
          <h2 id="aviso-sesion-titulo" className="modal__titulo">
            {vigencia.extensible ? t('session.expiringTitle') : t('session.ceilingTitle')}
          </h2>
          <p id="aviso-sesion-texto" className="aviso-sesion__texto">
            {vigencia.extensible ? t('session.expiringText') : t('session.ceilingText')}
          </p>
          <p className="aviso-sesion__cuenta" role="timer">
            {tiempo}
          </p>
          <div className="modal__acciones">
            <button className="boton boton--fantasma" type="button" onClick={onSalir}>
              {t('app.signOut')}
            </button>
            <button ref={principal} className="boton" type="button" onClick={contestar} disabled={ocupado}>
              {vigencia.extensible ? t('session.stay') : t('session.understood')}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
